'use client';

/**
 * Own-auth token store.
 *
 * Holds the JWT issued by /auth/register, /auth/login, /auth/google/callback,
 * and /auth/wealthorbit-sso.  Mirrors the value to a cookie so middleware.ts
 * can gate routes server-side; client code reads from the in-memory store via
 * `useAuthToken()` for synchronous access.
 *
 * The cookie is intentionally NOT HttpOnly so the same value can power both
 * the middleware check and the Authorization header on fetch — matching the
 * "Use sessionStorage or an HttpOnly cookie" guidance in auth-login.json.
 * SameSite=Lax + Secure (prod) keeps it CSRF-safe.
 */

const COOKIE_NAME = 'lexai_token';
const COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60; // 7 days, matches JWT exp default

type Listener = (token: string | null) => void;

let currentToken: string | null = null;
const listeners = new Set<Listener>();
let hydrated = false;

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.slice(COOKIE_NAME.length + 1);
  return value ? decodeURIComponent(value) : null;
}

function writeCookie(token: string | null): void {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secure = isHttps ? '; Secure' : '';
  if (token) {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  }
}

function hydrateFromCookie(): void {
  if (hydrated) return;
  hydrated = true;
  currentToken = readCookie();
}

export function getAuthToken(): string | null {
  hydrateFromCookie();
  return currentToken;
}

export function setAuthToken(token: string): void {
  hydrated = true;
  currentToken = token;
  writeCookie(token);
  listeners.forEach((l) => l(token));
}

export function clearAuthToken(): void {
  hydrated = true;
  currentToken = null;
  writeCookie(null);
  listeners.forEach((l) => l(null));
}

export function subscribeAuthToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const AUTH_COOKIE_NAME = COOKIE_NAME;
